import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { studentApi } from '@/api';
import { Icon } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

const DISMISS_KEY = 'jw.profile-nudge-dismissed';

/**
 * The student's profile, shared with the profile page and the dashboard (same
 * query key), so this costs nothing once either has loaded. Off until the
 * email is confirmed: every student endpoint refuses an unverified account.
 */
function useProfileCompletion() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['student-profile'],
    queryFn: studentApi.getProfile,
    enabled: Boolean(user?.is_email_verified),
    staleTime: 60_000,
  });
  return data?.profile_completion ?? null;
}

/** "70%" beside "Profile & account" in the menu, until the profile is complete. */
export function ProfileCompletionBadge() {
  const completion = useProfileCompletion();
  if (completion == null || completion >= 100) return null;
  return (
    <span
      className="bg-warning-50 text-warning-700 ring-warning-200 rounded-full px-1.5 py-px text-[10px] font-semibold tabular-nums ring-1 ring-inset"
      title="Your profile is not complete yet"
    >
      {completion}%
    </span>
  );
}

function wasDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * A slim reminder above every student page while the profile is incomplete.
 * Hidden on the profile page itself (the Completion card says what is
 * missing there) and dismissible for the rest of the session.
 */
export function ProfileCompletionBanner() {
  const completion = useProfileCompletion();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(wasDismissed);

  if (completion == null || completion >= 100 || dismissed) return null;
  if (pathname.startsWith(paths.student.profile)) return null;

  function dismiss() {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Private mode: it simply comes back on the next page load.
    }
  }

  return (
    <div className="border-brand-200 bg-brand-50/70 rounded-card mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 border px-4 py-3">
      <span
        aria-hidden="true"
        className="bg-surface text-brand-600 ring-brand-100 grid size-9 shrink-0 place-items-center rounded-lg ring-1"
      >
        <Icon name="userCheck" size={17} />
      </span>
      <div className="min-w-0 flex-[1_1_16rem]">
        <p className="text-ink text-sm font-semibold">Your profile is {completion}% complete</p>
        <p className="text-ink-muted text-xs">
          Companies see complete profiles first, and your matches get better as you fill it in.
        </p>
        <div
          className="bg-brand-100 mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full"
          role="progressbar"
          aria-valuenow={completion}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Profile completion"
        >
          <div className="bg-brand-600 h-full rounded-full" style={{ width: `${completion}%` }} />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Link
          to={paths.student.profile}
          className="bg-brand-600 hover:bg-brand-700 rounded-input inline-flex min-h-8 items-center gap-1.5 px-3 py-1 text-sm font-medium text-white shadow-xs transition"
        >
          Complete profile
          <Icon name="arrowRight" size={14} />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide this reminder"
          title="Hide this reminder"
          className="text-ink-muted hover:text-ink hover:bg-surface rounded-input grid size-8 place-items-center transition"
        >
          <Icon name="close" size={15} />
        </button>
      </div>
    </div>
  );
}
